import { useMemo, useRef } from 'react';
import { convertiUtsToDate, formatUniteArray } from '../../GrapheXT/GrapheXT';
import {
  SIGNAL_DOMAIN,
  LABEL_DOMAIN,
  SECTION_ALPHA,
} from '../utils/labelConstants';
import { buildAnnotation, buildEventTraces } from '../utils/plotlyBuilders';
import { mapApiLabel } from '../utils/labelApiMapper';
import { IA_CATEGORIES } from '../utils/iaCategories';
import { defaultColors } from '../../../../UI/ColorPlotly';

// Première couleur "réelle" parmi les candidats : ignore null, undefined ET chaîne vide.
// (?? ne filtre pas '' : une couleur API vide ferait passer la légende au noir alors que
//  Plotly applique sa colorway au tracé → légende et courbe désynchronisées.)
const firstRealColor = (...vals) =>
  vals.find((v) => typeof v === 'string' && v.trim() !== '') ?? null;

// Couleur effective d'une courbe : couleur explicite (API / config) sinon couleur du
// colorway Plotly par index — garantit que le tracé ET la légende custom partagent la même.
const curveColor = (gc, curve, i) =>
  firstRealColor(gc?.color, curve?.line?.color, curve?.color, curve?.marker?.color) ??
  defaultColors[i % defaultColors.length];

// Labels from configuration may arrive in raw API format (time_unix, label_text, source…)
// while localLabels are already normalized via mapApiLabel. This helper bridges the gap.
const normalizeLabel = (l) =>
  l.t != null && l.iconType != null ? l : mapApiLabel(l);

const usePlotlyData = (
  donnees,
  configuration,
  localLabels,
  localPhases,
  graphCurves,
  seuils = [],
  isLabelMode = false,
  chartWidthPx = 1000,
  labelBandHeightPx = 96
) => {
  const sectionsRef = useRef([]);
  const tRawRef = useRef([]);
  const labelsRawRef = useRef([]);

  const { allTraces, annMeta, annotations, layout, labelCategories, legendItems } = useMemo(() => {
    // ── Traces signal ─────────────────────────────────────────────────────────
    const signalTraces = donnees.map((curve, curveIndex) => {
      const tRaw = curve.t || curve.x || [];
      if (curveIndex === 0) tRawRef.current = tRaw;
      const gc = graphCurves?.[curveIndex] || {};
      const apiYaxis = (gc.yaxis || curve.yaxis || 'y1').toLowerCase();
      const unit = curve.engineering_unit || '';
      const nameStr   = typeof curve.name   === 'string' ? curve.name.trim()   : '';
      const prefixStr = typeof curve.prefix === 'string' ? curve.prefix.trim() : '';
      const displayName =
        nameStr   ? curve.name
        : prefixStr ? curve.prefix
        : curve.position_in_sensor != null ? `[${curve.position_in_sensor}]`
        : `Signal ${curveIndex + 1}`;
      const yFormatted = formatUniteArray(curve.y || [], unit);
      const text = yFormatted.map((yVal) =>
        [
          curve.sensor_name || '',
          `${displayName}: ${yVal}${unit ? ' ' + unit : ''}`,
        ].filter(Boolean).join('<br>')
      );
      return {
        ...curve,
        legendgroup: `signal:${curveIndex}`,
        type: gc.type || curve.type || 'scatter',
        mode: gc.mode || curve.mode || 'lines',
        x: convertiUtsToDate(tRaw),
        y: curve.y || [],
        yaxis: apiYaxis === 'y2' ? 'y3' : 'y',
        name:
          gc.name ||
          curve.name ||
          curve.sensor_name ||
          `Signal ${curveIndex + 1}`,
        text,
        hovertemplate: '%{text}<extra></extra>',
        line: {
          ...(curve.line || {}),
          color: curveColor(gc, curve, curveIndex),
          dash: gc.line_dash || curve.line?.dash,
          shape: gc.line_shape || curve.line?.shape,
        },
        meta: { formula: gc.formula || null },
        marker: { color: curveColor(gc, curve, curveIndex) },
      };
    });

    const isY2Curve = (c, i) =>
      (graphCurves?.[i]?.yaxis || c.yaxis || '').toLowerCase() === 'y2';
    const hasY2 = donnees.some(isY2Curve);

    const allYValues = donnees.flatMap((d) => d.y || []);
    const yMax = allYValues.length ? Math.max(...allYValues) : 1;
    const yMin = allYValues.length ? Math.min(...allYValues) : 0;
    const yRange = yMax - yMin || 1;

    // Per-axis ranges (Y1 = left, Y2/Y3 = right).
    const y1Values = donnees.filter((c, i) => !isY2Curve(c, i)).flatMap((d) => d.y || []);
    const y1Max = y1Values.length ? Math.max(...y1Values) : yMax;
    const y1Min = y1Values.length ? Math.min(...y1Values) : yMin;
    const y1Range = y1Max - y1Min || 1;

    const y2Values = donnees.filter((c, i) => isY2Curve(c, i)).flatMap((d) => d.y || []);
    const y2Max = y2Values.length ? Math.max(...y2Values) : 1;
    const y2Min = y2Values.length ? Math.min(...y2Values) : 0;
    const y2Range = y2Max - y2Min || 1;

    // Préférer .t (toujours en Unix secondes) sur .x qui peut contenir des date strings.
    const tBase = donnees[0]?.t || donnees[0]?.x || [];
    const tBaseNum = tBase.filter((v) => typeof v === 'number');
    const tStart = tBaseNum.length > 0 ? tBaseNum[0] : -Infinity;
    const tEnd   = tBaseNum.length > 0 ? tBaseNum[tBaseNum.length - 1] : Infinity;
    const markerSize = configuration.markerSize || 13;
    const shownLabels = new Set();

    // ── Labels ────────────────────────────────────────────────────────────────
    // Sensors configurés dans le graphe — extraits des formules graphCurves (#idSensor,pos#)
    const configuredSensorIds = new Set(
      (graphCurves || [])
        .map((gc) => {
          const nums = (gc.formula || '').match(/\d+/g)?.map(Number);
          return nums?.[0] ?? null;
        })
        .filter((id) => id != null)
    );

    // Plage temporelle du signal — robuste : utilise seulement les valeurs numériques de tBase
    const hasTimeRange = Number.isFinite(tStart) && Number.isFinite(tEnd) && tStart < tEnd;

    const labelsRaw = [
      ...(Array.isArray(configuration.labels)
        ? configuration.labels.map(normalizeLabel)
        : Array.isArray(configuration.events)
          ? configuration.events.map(normalizeLabel)
          : []),
      ...localLabels,
    ].filter((l) => {
      if (l.t == null || !Number.isFinite(Number(l.t))) return false;
      const t = Number(l.t);

      // Filtre temporel — afficher seulement les labels dans la plage du signal chargé
      if (hasTimeRange && (t < tStart || t > tEnd)) return false;

      // Filtre par capteur configuré dans le graphe
      if (l.idSensor == null) return true;
      if (configuredSensorIds.size === 0) return true;
      return configuredSensorIds.has(Number(l.idSensor));
    });
    // Limiter à 200 labels max pour éviter une surcharge des traces Plotly
    const MAX_LABELS = 200;
    const labelsFiltered = labelsRaw.length > MAX_LABELS
      ? labelsRaw.slice(0, MAX_LABELS)
      : labelsRaw;
    labelsRawRef.current = labelsFiltered;

    const { traces: labelTraces, annMeta: labelAnnMeta, images: labelImages, categories: labelCategories } = buildEventTraces({
      items: labelsFiltered,
      tRaw: tBase,
      yRaw: donnees[0]?.y || [],
      donnees,
      yMax,
      domain: LABEL_DOMAIN,
      yaxisKey: 'y2',
      markerSize,
      shownLabels,
      graphCurves,
    });

    // ── Sections ──────────────────────────────────────────────────────────────
    const sectionsRaw = [
      ...(Array.isArray(configuration.sections) ? configuration.sections : []),
      ...localPhases,
    ].filter(
      (s) =>
        s.tStart != null &&
        Number.isFinite(Number(s.tStart)) &&
        s.tEnd != null &&
        Number.isFinite(Number(s.tEnd))
    );
    sectionsRef.current = sectionsRaw;

    const sectionShapes = sectionsRaw.map((sec) => {
      const x0 = convertiUtsToDate([sec.tStart])[0];
      const x1 = convertiUtsToDate([sec.tEnd])[0];
      const color = sec.color || '#888888';
      return {
        type: 'rect',
        xref: 'x',
        yref: 'paper',
        x0,
        x1,
        y0: SIGNAL_DOMAIN[0],
        y1: SIGNAL_DOMAIN[1],
        fillcolor: color + SECTION_ALPHA,
        line: { width: 0 },
        layer: 'below',
      };
    });

    const sectionAnnotations = sectionsRaw.map((sec) => {
      const xStart = convertiUtsToDate([sec.tStart])[0];
      const color = sec.color || '#888888';
      const title = sec.label
        ? `<b>${sec.label}</b>`
        : '<i style="color:#aaa">Phase</i>';
      return {
        x: xStart,
        xref: 'x',
        xanchor: 'left',
        y: SIGNAL_DOMAIN[1],
        yref: 'paper',
        yanchor: 'top',
        text: `${title} <span style="font-size:10px">✏</span>`,
        showarrow: false,
        font: { size: 11, color, family: 'Montserrat, sans-serif' },
        bgcolor: 'rgba(255,255,255,0.88)',
        bordercolor: color,
        borderwidth: 1,
        borderpad: 4,
        captureevents: true,
      };
    });

    const bandShapes = [
      {
        type: 'rect',
        xref: 'paper',
        yref: 'paper',
        x0: 0,
        x1: 1,
        y0: LABEL_DOMAIN[0] - 0.01,
        y1: LABEL_DOMAIN[1],
        fillcolor: 'rgba(240,243,255,0.6)',
        line: { width: 1, color: '#d0d5ee' },
        layer: 'below',
      },
    ];

    const plotYMax = yMax + yRange * 0.08;

    // Lignes de connexion pointillées — groupées par legendGroup pour suivre la visibilité légende.
    const dotByGroup = new Map();
    labelAnnMeta.forEach((ev) => {
      if (!dotByGroup.has(ev.legendGroup)) {
        dotByGroup.set(ev.legendGroup, { xSig: [], ySig: [], xLab: [], yLab: [] });
      }
      const g = dotByGroup.get(ev.legendGroup);
      g.xSig.push(ev.xDate, ev.xDate, null);
      g.ySig.push(ev.yCurveVal, plotYMax * 100, null);
      g.xLab.push(ev.xDate, ev.xDate, null);
      g.yLab.push(0, ev.markerY, null);
    });
    const dotLineTraces = [];
    dotByGroup.forEach(({ xSig, ySig, xLab, yLab }, legendGroup) => {
      dotLineTraces.push({
        type: 'scatter', mode: 'lines',
        showlegend: false, hoverinfo: 'skip', name: '',
        legendgroup: legendGroup,
        xaxis: 'x', yaxis: 'y',
        x: xSig, y: ySig,
        line: { color: '#b0b8c9', width: 1.2, dash: 'dot' },
      });
      dotLineTraces.push({
        type: 'scatter', mode: 'lines',
        showlegend: false, hoverinfo: 'skip', name: '',
        legendgroup: legendGroup,
        xaxis: 'x', yaxis: 'y2',
        x: xLab, y: yLab,
        line: { color: '#b0b8c9', width: 1.2, dash: 'dot' },
      });
    });

    // ── Seuils / Alarmes ─────────────────────────────────────────────────────
    // Couleur calculée par alarme (clé legendgroup) — réutilisée par la légende custom
    // pour garantir que la pastille de légende a EXACTEMENT la même couleur que le tracé.
    const alarmColorByKey = new Map();
    const seuilTraces = [];
    for (const seuilGroup of seuils) {
      if (seuilGroup.show_alarms !== undefined && parseInt(seuilGroup.show_alarms) === 0) continue;
      let showLegend = true;
      for (const seuilData of (seuilGroup.data || [])) {
        if (
          !Array.isArray(seuilData.x) || !Array.isArray(seuilData.y) ||
          !seuilData.x.length || !seuilData.y.length ||
          seuilData.x[0] == null || seuilData.y[0] == null
        ) continue;

        // line_color peut être sur le seuilData (priorité) ou le groupe — même logique que GrapheXT
        const lc = (seuilData.line_color || seuilGroup.line_color || '').trim();
        const lcKey = (lc.toLowerCase().startsWith('high ') || lc.toLowerCase().startsWith('low '))
          ? lc.split(' ')[1].toLowerCase()
          : lc.toLowerCase();
        let linecolor;
        switch (lcKey) {
          case 'black': linecolor = 'black'; break;
          case 'red':   linecolor = '#f70606'; break;
          case 'amber': linecolor = '#ffa233'; break;
          case 'blue':  linecolor = '#3390ff'; break;
          default:      linecolor = 'green'; break;
        }

        const sensorName = seuilGroup.sensor_name || '';
        const alarmKey = `alarme:${sensorName}`;
        // Mémorise la couleur du PREMIER tracé de cette alarme (celui qui porte la légende)
        if (!alarmColorByKey.has(alarmKey)) alarmColorByKey.set(alarmKey, linecolor);

        // Étendre le seuil jusqu'aux extrémités du signal pour qu'il soit toujours tracé
        // jusqu'au dernier point de données, même si l'API retourne une plage plus courte.
        const tStart = tBase.length > 0 ? tBase[0] : (seuilData.x?.[0] ?? 0);
        const tEnd   = tBase.length > 0 ? tBase[tBase.length - 1] : (seuilData.x?.[seuilData.x.length - 1] ?? 0);
        const yVal   = seuilData.y?.[0] ?? 0;
        const seuilX = convertiUtsToDate([tStart, tEnd]);
        const seuilY = [yVal, seuilData.y?.[seuilData.y.length - 1] ?? yVal];

        seuilTraces.push({
          type: 'scatter',
          xaxis: 'x',
          yaxis: 'y',
          x: seuilX,
          y: seuilY,
          name: `[Alarme] ${sensorName}`,
          legendgroup: `alarme:${sensorName}`,
          showlegend: showLegend,
          line: { ...(seuilGroup.line || {}), color: linecolor, dash: 'solid' },
          hovertemplate: `%{y}<extra>[Alarme] ${sensorName}</extra>`,
        });
        showLegend = false;
      }
    }

    // ── Hover phantoms ────────────────────────────────────────────────────────
    const N_HOVER = 20;
    const yHover = yMax + yRange * 0.04;
    const N_LABEL_HOVER = 10;

    // Phases hover — 1 trace au lieu de N (points de toutes les phases dans un seul scatter).
    const phX = [], phY = [], phCd = [];
    sectionsRaw.forEach((sec) => {
      const phaseData = { type: 'phase', tStart: sec.tStart, tEnd: sec.tEnd, label: sec.label, color: sec.color };
      for (let i = 0; i < N_HOVER; i++) {
        const t = sec.tStart + ((sec.tEnd - sec.tStart) * i) / (N_HOVER - 1);
        phX.push(convertiUtsToDate([Math.round(t)])[0]);
        phY.push(yHover);
        phCd.push(phaseData);
      }
    });
    const phaseHoverTraces = phX.length > 0 ? [{
      type: 'scatter', mode: 'markers',
      hoverinfo: 'none', showlegend: false, name: '',
      xaxis: 'x', yaxis: 'y',
      x: phX, y: phY,
      marker: { size: 18, color: 'rgba(0,0,0,0)', symbol: 'square', line: { width: 0 } },
      customdata: phCd,
    }] : [];

    // Labels hover — 1 trace au lieu de N (tous les points de hover dans un seul scatter).
    const lhX = [], lhY = [], lhCd = [];
    labelAnnMeta.forEach((ev, annMetaIdx) => {
      const R = 0.3;
      const yLow  = Math.max(0, ev.markerY - R);
      const yHigh = Math.min(1, ev.markerY + R);
      const cd = { type: 'label', t: ev.t, annMetaIdx, itemIdx: ev.itemIdx };
      for (let i = 0; i < N_LABEL_HOVER; i++) {
        lhX.push(ev.xDate);
        lhY.push(yLow + (yHigh - yLow) * (i / (N_LABEL_HOVER - 1)));
        lhCd.push(cd);
      }
    });
    const labelHoverTraces = lhX.length > 0 ? [{
      type: 'scatter', mode: 'markers',
      hoverinfo: 'none', showlegend: false, name: '',
      xaxis: 'x', yaxis: 'y2',
      x: lhX, y: lhY,
      marker: { size: 10, color: 'rgba(0,0,0,0)', symbol: 'square', line: { width: 0 } },
      customdata: lhCd,
    }] : [];

    // ── Layout ────────────────────────────────────────────────────────────────
    const layout = {
      title: {
        text: `${configuration.title || ''}${configuration.subtitle ? '<br>' + configuration.subtitle : ''}`,
      },
      hovermode: 'x',
      font: { family: 'Montserrat, sans-serif', size: 12 },
      paper_bgcolor: 'white',
      plot_bgcolor: 'white',
      xaxis: {
        title: { text: configuration.xaxis?.title || 't' },
        type: 'date',
        domain: [0, 1],
        showgrid: true,
        zeroline: false,
        anchor: 'y',
      },
      yaxis: {
        title: { text: configuration.yaxis?.title || '' },
        domain: SIGNAL_DOMAIN,
        range: [y1Min - y1Range * 0.05, y1Max + y1Range * 0.08],
        showgrid: true,
        zeroline: false,
      },
      yaxis2: {
        domain: LABEL_DOMAIN,
        range: [0, 1],
        fixedrange: true,
        visible: false,
        anchor: 'x',
      },
      ...(hasY2 ? {
        yaxis3: {
          title: { text: configuration.yaxis2?.title || '' },
          domain: SIGNAL_DOMAIN,
          overlaying: 'y',
          side: 'right',
          range: [y2Min - y2Range * 0.05, y2Max + y2Range * 0.08],
          showgrid: false,
          zeroline: false,
        },
      } : {}),
      shapes: [...sectionShapes, ...bandShapes],
      images: labelImages,
      annotations: [...sectionAnnotations],
      showlegend: false,
      margin: { l: 80, r: hasY2 ? 280 : 230, t: 60, b: 60 },
    };

    const annotations = labelAnnMeta.map((ev) => {
      const paperY =
        LABEL_DOMAIN[0] + (LABEL_DOMAIN[1] - LABEL_DOMAIN[0]) * ev.markerY;
      return {
        ...buildAnnotation({
          xDate: ev.xDate,
          paperY,
          label: ev.label,
          dateStr: ev.dateStr,
          note: ev.note,
          color: ev.style.color,
          valLine: ev.valLine,
        }),
        _t: ev.t, // timestamp pour retrouver l'index depuis usePlotlyEvents
      };
    });

    // ── Legend items (pour la légende custom React) ───────────────────────────
    const signalLegendItems = donnees.map((curve, i) => {
      const gc = graphCurves?.[i] || {};
      return {
        id: `signal:${i}`,
        name: gc.name || curve.name || curve.sensor_name || `Signal ${i + 1}`,
        type: 'signal',
        color: curveColor(gc, curve, i),
        dash: gc.line_dash || curve.line?.dash || 'solid',
      };
    });

    const alarmLegendItems = [];
    const seenAlarms = new Set();
    for (const sg of seuils) {
      if (sg.show_alarms !== undefined && parseInt(sg.show_alarms) === 0) continue;
      const sensorName = sg.sensor_name || '';
      const key = `alarme:${sensorName}`;
      if (!seenAlarms.has(key)) {
        seenAlarms.add(key);
        // Couleur identique au tracé : récupérée du mapa rempli pendant seuilTraces.
        // Si l'alarme n'a produit aucun tracé valide, on ne l'ajoute pas à la légende.
        if (!alarmColorByKey.has(key)) continue;
        alarmLegendItems.push({
          id: key,
          name: `[Alarme] ${sensorName}`,
          type: 'alarm',
          color: alarmColorByKey.get(key),
          dash: 'solid',
        });
      }
    }

    const plainLabelItems = [];
    const seenLabGroups = new Set();
    labelAnnMeta.forEach((ev) => {
      if (ev.legendGroup.startsWith('label:') && !seenLabGroups.has(ev.legendGroup)) {
        seenLabGroups.add(ev.legendGroup);
        plainLabelItems.push({
          id: ev.legendGroup,
          name: ev.label,
          fullName: ev.legendGroup.replace('label:', ''),
          type: 'label',
          color: ev.style.color,
        });
      }
    });

    const categoryLegendItems = labelCategories.map((cat) => ({
      id: `category:${cat.iconType}`,
      name: cat.name,
      type: 'category',
      color: cat.color,
      iconType: cat.iconType,
    }));

    const legendItems = [...signalLegendItems, ...alarmLegendItems, ...plainLabelItems, ...categoryLegendItems];

    return {
      annMeta: labelAnnMeta,
      allTraces: [
        ...signalTraces,
        ...seuilTraces,
        ...labelTraces,
        ...phaseHoverTraces,
        ...labelHoverTraces,
        ...dotLineTraces,
      ],
      annotations,
      layout,
      labelCategories,
      legendItems,
    };
  }, [donnees, configuration, localLabels, localPhases, graphCurves, seuils, isLabelMode, chartWidthPx, labelBandHeightPx]);

  return { allTraces, annMeta, annotations, layout, sectionsRef, tRawRef, labelsRawRef, labelCategories, legendItems };
};

export default usePlotlyData;
